import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { MoneyContainer, MoneyEvent } from 'src/typings';
import { environment } from 'src/environments/environment';
import { BankStorageKindEnum, MoneyTypeEnum } from 'src/enums';
import { normalizeMoneyContainer, normalizeMoneyEvent } from './money-flow.utils';

export interface SearchMoneyEventDto {
  include_legacy?: boolean;
  id?: number;
  container_id?: number;
  money_day_id?: number;
  manager_user_id?: number;
  actor_user_id?: number;
  warehouse_id?: number;
  money_type?: MoneyTypeEnum;
  container_storage_kind?: BankStorageKindEnum;
  operation_group?: MoneyEventOperationGroup;
  category?: string;
  event_type?: string;
  source_doc_type?: string;
  source_doc_id?: number;
  business_date?: Date;
  date_from?: Date;
  date_to?: Date;
  amount_from?: number;
  amount_to?: number;
  limit?: number;
  offset?: number;
}

export type MoneyEventOperationGroup =
  | 'INFLOW'
  | 'OUTFLOW'
  | 'OPERATING_EXPENSE'
  | 'REFUND'
  | 'TRANSFER';

export interface MoneyEventSearchSummary {
  accounting_basis?: 'LEDGER';
  container_balance?: number | null;
  event_count: number;
  total_inflow: number;
  total_outflow: number;
  net_amount: number;
  operating_expense_total: number;
  refund_total: number;
  transfer_total: number;
}

export interface MoneyEventWarehouseSummary {
  warehouse_id: number | null;
  warehouse_name: string | null;
  event_count: number;
  expense_total: number;
  categories: MoneyEventWarehouseCategorySummary[];
}

export interface MoneyEventWarehouseCategorySummary {
  category: string;
  event_count: number;
  expense_total: number;
}

export interface SearchMoneyContainerDto {
  money_type?: MoneyTypeEnum;
  storage_kind?: BankStorageKindEnum;
  warehouse_id?: number;
  owner_user_id?: number;
  is_operational?: boolean;
  limit?: number;
  offset?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MoneyEventService {
  constructor(private http: HttpClient) {}

  search(payload: SearchMoneyEventDto) {
    return this.http
      .post<{
        items: MoneyEvent[];
        total: number;
        summary?: MoneyEventSearchSummary;
        warehouse_summary?: MoneyEventWarehouseSummary[];
      }>(`${environment.BACKEND}/money-event/search`, payload)
      .pipe(
        map((response) => ({
          ...response,
          items: (response.items || []).map((item) => normalizeMoneyEvent(item)),
          summary: {
            accounting_basis: response.summary?.accounting_basis,
            container_balance:
              response.summary?.container_balance == null
                ? null
                : Number(response.summary.container_balance),
            event_count: Number(response.summary?.event_count || 0),
            total_inflow: Number(response.summary?.total_inflow || 0),
            total_outflow: Number(response.summary?.total_outflow || 0),
            net_amount: Number(response.summary?.net_amount || 0),
            operating_expense_total: Number(response.summary?.operating_expense_total || 0),
            refund_total: Number(response.summary?.refund_total || 0),
            transfer_total: Number(response.summary?.transfer_total || 0)
          },
          warehouse_summary: (response.warehouse_summary || []).map((row) => ({
            ...row,
            warehouse_id: row.warehouse_id ? Number(row.warehouse_id) : null,
            event_count: Number(row.event_count || 0),
            expense_total: Number(row.expense_total || 0),
            categories: (row.categories || []).map((category) => ({
              ...category,
              event_count: Number(category.event_count || 0),
              expense_total: Number(category.expense_total || 0)
            }))
          }))
        }))
      );
  }

  getOne(id: number) {
    return this.http
      .get<MoneyEvent>(`${environment.BACKEND}/money-event/${id}`)
      .pipe(map((doc) => normalizeMoneyEvent(doc)));
  }

  searchContainers(payload: SearchMoneyContainerDto) {
    return this.http
      .post<{ data: MoneyContainer[]; length: number }>(
        `${environment.BACKEND}/money-event/containers/search`,
        payload
      )
      .pipe(
        map((response) => ({
          items: (response.data || []).map((item) => normalizeMoneyContainer(item)),
          total: Number(response.length || 0)
        }))
      );
  }
}
